import type { HTMLAttributes, ReactNode } from 'react';

/**
 * Card surface primitive. Replaces ~30 inline `bg-white rounded border
 * border-slate-200 p-{3,4,5}` usages with drifting padding. Use `padding` to
 * pick the rhythm (`md` = 4, matches most dashboards; `sm` = 3 for filter
 * strips; `lg` = 5 for dense forms). `tone="muted"` renders a subtle slate-50
 * surface for nested stat tiles.
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: 'sm' | 'md' | 'lg' | 'none';
  tone?: 'default' | 'muted';
  children: ReactNode;
}

const PADDING: Record<'sm' | 'md' | 'lg' | 'none', string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
};

const TONE: Record<'default' | 'muted', string> = {
  default: 'bg-white',
  muted: 'bg-slate-50',
};

export default function Card({
  padding = 'md',
  tone = 'default',
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      {...rest}
      className={[
        'rounded-lg border border-slate-200',
        TONE[tone],
        PADDING[padding],
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}
